// Definizione centralizzata dei ruoli utente
// Usare queste costanti invece di stringhe hardcoded per evitare errori di battitura

// Definizione dei tipi per i ruoli
export const ROLES = {
	SYS_ADMIN: 'SysAdmin',
	ADMIN: 'Admin',
	MANAGER: 'Manager',
	HR: 'HR',
	USER: 'User',
} as const;

// Tipo derivato dai valori di ROLES
export type Role = (typeof ROLES)[keyof typeof ROLES];

/**
 * Array di ruoli preconfigurati per casi d'uso comuni
 */
export const ROLE_GROUPS = {
	// Solo amministratori di sistema
	SYS_ADMIN_ONLY: [ROLES.SYS_ADMIN],

	// Amministratori di sistema e admin
	ADMIN_LEVEL: [ROLES.SYS_ADMIN, ROLES.ADMIN],

	// Tutti i ruoli manageriali
	MANAGEMENT_LEVEL: [ROLES.SYS_ADMIN, ROLES.ADMIN, ROLES.MANAGER],

	// Tutti i ruoli (inclusi utenti finali)
	EVERYONE: [ROLES.SYS_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.USER, ROLES.HR],

	// Solo ruoli HR (gestiti da SysAdmin)
	HR_ONLY: [ROLES.HR],

	// SysAdmin e HR (per presenze/attendances)
	SYS_ADMIN_HR: [ROLES.SYS_ADMIN, ROLES.HR],
} as const;

// Tipo derivato dai gruppi di ruoli
export type RoleGroup = (typeof ROLE_GROUPS)[keyof typeof ROLE_GROUPS];
export type RoleArray = readonly Role[];

/**
 * Verifica se un array di ruoli contiene un ruolo specifico
 * @param userRoles - Array dei ruoli dell'utente
 * @param requiredRole - Ruolo richiesto (usare ROLES.*)
 * @returns true se l'utente ha il ruolo richiesto
 */
export function hasRole(userRoles: readonly string[], requiredRole: Role): boolean {
	return Array.isArray(userRoles) && userRoles.includes(requiredRole);
}

/**
 * Verifica se un utente ha almeno uno dei ruoli richiesti
 * @param userRoles - Array dei ruoli dell'utente
 * @param requiredRoles - Array dei ruoli richiesti (usare ROLES.*)
 * @returns true se l'utente ha almeno uno dei ruoli
 */
export function hasAnyRole(userRoles: readonly string[], requiredRoles: readonly Role[]): boolean {
	if (!Array.isArray(userRoles) || !Array.isArray(requiredRoles)) {
		return false;
	}
	return requiredRoles.some((role) => userRoles.includes(role));
}

/**
 * Verifica se un utente ha tutti i ruoli richiesti
 * @param userRoles - Array dei ruoli dell'utente
 * @param requiredRoles - Array dei ruoli richiesti (usare ROLES.*)
 * @returns true se l'utente ha tutti i ruoli
 */
export function hasAllRoles(userRoles: readonly string[], requiredRoles: readonly Role[]): boolean {
	if (!Array.isArray(userRoles) || !Array.isArray(requiredRoles)) {
		return false;
	}
	return requiredRoles.every((role) => userRoles.includes(role));
}
